<p-toast />

<div class="mb-4">
    <div class="font-semibold text-2xl tracking-tight">
        Fake review comments
    </div>
    <div class="text-sm text-muted-color mt-1">
        Comment templates with rating bounds. The platform picks templates whose
        rating range covers the target rating when generating a fake review.
    </div>
</div>

<div class="card">
    <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
            <p-tab value="add">
                <span class="flex items-center gap-2">
                    <i class="pi pi-plus"></i> Add new
                </span>
            </p-tab>
            <p-tab value="manage">
                <span class="flex items-center gap-2">
                    <i class="pi pi-pencil"></i> Manage
                </span>
            </p-tab>
        </p-tablist>
        <p-tabpanels>
            <p-tabpanel value="add">
                @if (error()) {
                <p-message
                    severity="error"
                    [text]="error()!"
                    styleClass="mb-3"
                />
                }

                <p-table
                    [value]="rows()"
                    dataKey="id"
                    [rowTrackBy]="trackByRowId"
                    [showGridlines]="true"
                    responsiveLayout="scroll"
                    [paginator]="rows().length > pageSize()"
                    [rows]="pageSize()"
                    [first]="firstIndex()"
                    [rowsPerPageOptions]="pageSizeOptions"
                    (onPage)="onPage($event)"
                >
                    <ng-template #header>
                        <tr>
                            <th>Text *</th>
                            <th style="width: 18rem">
                                Tags * (comma-separated)
                            </th>
                            <th style="width: 8rem">Min rating *</th>
                            <th style="width: 8rem">Max rating *</th>
                            <th style="width: 4rem"></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-row>
                        <tr>
                            <td>
                                <textarea
                                    pTextarea
                                    rows="3"
                                    autoResize
                                    class="w-full"
                                    placeholder="Comment text"
                                    [ngModel]="row.Text"
                                    (ngModelChange)="row.Text = $event ?? ''"
                                ></textarea>
                            </td>
                            <td>
                                <input
                                    pInputText
                                    type="text"
                                    class="w-full"
                                    placeholder="fun, addictive, fast"
                                    [ngModel]="row.TagsRaw"
                                    (ngModelChange)="row.TagsRaw = $event ?? ''"
                                />
                            </td>
                            <td>
                                <p-inputnumber
                                    [ngModel]="row.MinRating"
                                    (ngModelChange)="row.MinRating = $event ?? 1"
                                    [min]="1"
                                    [max]="5"
                                    [showButtons]="true"
                                    buttonLayout="horizontal"
                                    spinnerMode="horizontal"
                                    decrementButtonClass="p-button-secondary"
                                    incrementButtonClass="p-button-secondary"
                                    styleClass="w-full"
                                />
                            </td>
                            <td>
                                <p-inputnumber
                                    [ngModel]="row.MaxRating"
                                    (ngModelChange)="row.MaxRating = $event ?? 5"
                                    [min]="1"
                                    [max]="5"
                                    [showButtons]="true"
                                    buttonLayout="horizontal"
                                    spinnerMode="horizontal"
                                    decrementButtonClass="p-button-secondary"
                                    incrementButtonClass="p-button-secondary"
                                    styleClass="w-full"
                                />
                            </td>
                            <td>
                                <p-button
                                    icon="pi pi-trash"
                                    severity="danger"
                                    [text]="true"
                                    size="small"
                                    pTooltip="Remove row"
                                    (onClick)="requestRemoveRow(row)"
                                />
                            </td>
                        </tr>
                    </ng-template>
                </p-table>

                <div
                    class="flex items-center justify-between gap-2 mt-3 flex-wrap"
                >
                    <div class="flex items-center gap-2">
                        <p-button
                            label="Add row"
                            icon="pi pi-plus"
                            severity="secondary"
                            outlined
                            (onClick)="addRow()"
                        />
                        <p-button
                            label="Import JSON"
                            icon="pi pi-upload"
                            severity="secondary"
                            outlined
                            (onClick)="openImport()"
                        />
                        <p-button
                            label="Clear"
                            icon="pi pi-times"
                            severity="secondary"
                            [text]="true"
                            [disabled]="saving()"
                            (onClick)="requestClear()"
                        />
                    </div>
                    <div class="flex items-center gap-3">
                        <span class="text-sm text-muted-color">
                            Total rows: <strong>{{ rows().length }}</strong> ·
                            Ready to save: <strong>{{ validCount() }}</strong>
                        </span>
                        <p-button
                            label="Save comments"
                            icon="pi pi-check"
                            [loading]="saving()"
                            [disabled]="validCount() === 0"
                            (onClick)="save()"
                        />
                    </div>
                </div>
            </p-tabpanel>

            <p-tabpanel value="manage">
                <div
                    class="flex items-center justify-between gap-2 mb-3 flex-wrap"
                >
                    <div class="flex items-center gap-2 flex-wrap">
                        <p-iconfield>
                            <p-inputicon styleClass="pi pi-search" />
                            <input
                                pInputText
                                type="text"
                                placeholder="Search by text…"
                                [ngModel]="manageSearch()"
                                (ngModelChange)="manageSearch.set($event ?? '')"
                            />
                        </p-iconfield>
                        <p-multiselect
                            [options]="availableTags()"
                            [ngModel]="selectedTags()"
                            (ngModelChange)="selectedTags.set($event ?? [])"
                            placeholder="Tags"
                            [filter]="true"
                            [showHeader]="true"
                            [showClear]="true"
                            display="chip"
                            styleClass="min-w-56"
                            appendTo="body"
                        />
                        @if (
                            manageSearch().length > 0 ||
                            selectedTags().length > 0
                        ) {
                        <p-button
                            label="Clear"
                            icon="pi pi-filter-slash"
                            severity="secondary"
                            [text]="true"
                            size="small"
                            (onClick)="clearFilters()"
                        />
                        }
                    </div>
                    <div class="flex items-center gap-3">
                        <span class="text-sm text-muted-color">
                            Showing
                            <strong>{{ filteredComments().length }}</strong>
                            of
                            <strong>{{ allComments().length }}</strong>
                        </span>
                        <p-button
                            label="Refresh"
                            icon="pi pi-refresh"
                            severity="secondary"
                            [text]="true"
                            size="small"
                            [loading]="manageLoading()"
                            (onClick)="reloadComments()"
                        />
                    </div>
                </div>

                <div
                    class="flex items-center gap-4 mb-3 text-sm text-muted-color flex-wrap"
                >
                    <span
                        pTooltip="Comments whose rating range covers each star value"
                        tooltipPosition="bottom"
                    >
                        Covers:
                    </span>
                    <span>1★: {{ commentsStat().PerRating.Rating1 }}</span>
                    <span>2★: {{ commentsStat().PerRating.Rating2 }}</span>
                    <span>3★: {{ commentsStat().PerRating.Rating3 }}</span>
                    <span>4★: {{ commentsStat().PerRating.Rating4 }}</span>
                    <span>5★: {{ commentsStat().PerRating.Rating5 }}</span>
                </div>

                @if (manageError()) {
                <p-message
                    severity="error"
                    [text]="manageError()!"
                    styleClass="mb-3"
                />
                }

                <p-table
                    [value]="filteredComments()"
                    dataKey="Id"
                    [showGridlines]="true"
                    responsiveLayout="scroll"
                    [loading]="manageLoading()"
                    [paginator]="true"
                    [rows]="20"
                    [rowsPerPageOptions]="[10, 20, 50, 100]"
                >
                    <ng-template #header>
                        <tr>
                            <th>Text</th>
                            <th style="width: 18rem">Tags</th>
                            <th style="width: 7rem">Rating</th>
                            <th style="width: 8rem"></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-comment>
                        <tr>
                            <td class="whitespace-pre-wrap">
                                {{ comment.Text }}
                            </td>
                            <td>
                                <div class="flex flex-wrap gap-1">
                                    @for (tag of comment.Tags; track tag) {
                                    <span
                                        class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-700"
                                    >
                                        {{ tag }}
                                    </span>
                                    }
                                </div>
                            </td>
                            <td>
                                {{ comment.MinRating }}–{{ comment.MaxRating }}
                            </td>
                            <td>
                                <div class="flex items-center gap-1">
                                    <p-button
                                        icon="pi pi-pencil"
                                        severity="secondary"
                                        [text]="true"
                                        size="small"
                                        pTooltip="Edit"
                                        (onClick)="openEdit(comment)"
                                    />
                                    <p-button
                                        icon="pi pi-trash"
                                        severity="danger"
                                        [text]="true"
                                        size="small"
                                        pTooltip="Delete"
                                        (onClick)="requestDelete(comment)"
                                    />
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td
                                colspan="4"
                                class="text-center text-muted-color py-4"
                            >
                                @if (manageLoading()) { Loading… } @else if
                                (allComments().length === 0) { No comments. }
                                @else { No matches. }
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </p-tabpanel>
        </p-tabpanels>
    </p-tabs>
</div>

<p-dialog
    [visible]="importDialogVisible()"
    (visibleChange)="importDialogVisible.set($event)"
    header="Import comments (JSON)"
    [modal]="true"
    [style]="{ width: '48rem', maxWidth: '95vw' }"
    [draggable]="false"
    [resizable]="false"
>
    <div class="flex flex-col gap-3">
        <p class="text-sm text-muted-color m-0">
            Paste a JSON array, drop a <code>.json</code> file, or pick one.
            Parsed rows are appended to the table — review and click Save to
            insert into the pool.
        </p>

        <div
            class="rounded border-2 border-dashed p-2 transition-colors"
            [class.border-primary-500]="importDragOver()"
            [class.bg-primary-50]="importDragOver()"
            [class.border-surface-300]="!importDragOver()"
            [class.dark:border-surface-600]="!importDragOver()"
            (dragover)="onImportDragOver($event)"
            (dragleave)="onImportDragLeave()"
            (drop)="onImportDrop($event)"
        >
            <textarea
                pTextarea
                rows="12"
                class="w-full font-mono text-xs"
                spellcheck="false"
                [placeholder]="importExample"
                [ngModel]="importJsonText()"
                (ngModelChange)="importJsonText.set($event ?? '')"
            ></textarea>
        </div>

        <div class="flex items-center justify-between gap-2 flex-wrap">
            <label class="inline-flex">
                <input
                    #fileInput
                    type="file"
                    accept="application/json,.json"
                    class="hidden"
                    (change)="onImportFileChange($event)"
                />
                <p-button
                    label="Load file…"
                    icon="pi pi-file"
                    severity="secondary"
                    outlined
                    size="small"
                    (onClick)="fileInput.click()"
                />
            </label>
            <span class="text-xs text-muted-color">
                Expected shape matches the example shown as placeholder.
            </span>
        </div>

        @if (importError()) {
        <p-message severity="error" [text]="importError()!" styleClass="m-0" />
        }
    </div>

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="closeImport()"
        />
        <p-button
            label="Import"
            icon="pi pi-check"
            (onClick)="confirmImport()"
        />
    </ng-template>
</p-dialog>

<p-dialog
    [visible]="clearConfirmVisible()"
    (visibleChange)="clearConfirmVisible.set($event)"
    header="Clear all rows?"
    [modal]="true"
    [style]="{ width: '28rem' }"
    [draggable]="false"
    [resizable]="false"
>
    <p class="m-0 text-sm">
        Discard {{ rows().length }} row(s) currently in the table? This only
        affects the local working set — already-saved comments in the pool are
        not deleted.
    </p>

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="cancelClear()"
        />
        <p-button
            label="Clear"
            icon="pi pi-trash"
            severity="danger"
            (onClick)="confirmClear()"
        />
    </ng-template>
</p-dialog>

<p-dialog
    [visible]="removeConfirmVisible()"
    (visibleChange)="removeConfirmVisible.set($event)"
    header="Remove this row?"
    [modal]="true"
    [style]="{ width: '32rem' }"
    [draggable]="false"
    [resizable]="false"
>
    <div class="flex flex-col gap-3">
        <p class="m-0 text-sm">
            This row has content that will be lost. @if
            (removeConfirmRow()?.Text?.trim()) {
            <span class="block mt-2 text-xs text-muted-color">
                Text: <strong>{{ removeConfirmRow()!.Text }}</strong>
            </span>
            }
        </p>

        <div class="flex items-center gap-2">
            <p-checkbox
                inputId="comment-remove-dont-ask"
                [binary]="true"
                [ngModel]="removeConfirmDontAsk()"
                (ngModelChange)="removeConfirmDontAsk.set($event)"
            />
            <label for="comment-remove-dont-ask" class="text-sm cursor-pointer">
                Don't ask in this session
            </label>
        </div>
    </div>

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="cancelRemoveRow()"
        />
        <p-button
            label="Remove"
            icon="pi pi-trash"
            severity="danger"
            (onClick)="confirmRemoveRow()"
        />
    </ng-template>
</p-dialog>

<p-dialog
    [visible]="editDialogVisible()"
    (visibleChange)="editDialogVisible.set($event)"
    header="Edit comment"
    [modal]="true"
    [style]="{ width: '52rem', maxWidth: '95vw' }"
    [draggable]="false"
    [resizable]="false"
    (onHide)="closeEdit()"
>
    @if (editForm(); as form) {
    <div class="flex flex-col gap-3">
        @if (editError()) {
        <p-message
            severity="error"
            [text]="editError()!"
            styleClass="m-0"
        />
        }

        <div class="flex flex-col gap-1">
            <label class="text-sm font-medium" for="edit-comment-text">
                Text
            </label>
            <textarea
                pTextarea
                id="edit-comment-text"
                rows="4"
                autoResize
                class="w-full"
                [ngModel]="form.Text"
                (ngModelChange)="updateEditText($event ?? '')"
            ></textarea>
        </div>

        <div class="flex flex-col gap-1">
            <label class="text-sm font-medium" for="edit-comment-tags">
                Tags (comma-separated)
            </label>
            <input
                pInputText
                id="edit-comment-tags"
                type="text"
                [ngModel]="form.TagsRaw"
                (ngModelChange)="updateEditTagsRaw($event ?? '')"
            />
        </div>

        <div class="flex items-end gap-3">
            <div class="flex flex-col gap-1 flex-1">
                <label
                    class="text-sm font-medium"
                    for="edit-comment-min-rating"
                >
                    Min rating
                </label>
                <p-inputnumber
                    inputId="edit-comment-min-rating"
                    [ngModel]="form.MinRating"
                    (ngModelChange)="updateEditMinRating($event ?? ratingMin)"
                    [min]="ratingMin"
                    [max]="ratingMax"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    styleClass="w-full"
                />
            </div>
            <div class="flex flex-col gap-1 flex-1">
                <label
                    class="text-sm font-medium"
                    for="edit-comment-max-rating"
                >
                    Max rating
                </label>
                <p-inputnumber
                    inputId="edit-comment-max-rating"
                    [ngModel]="form.MaxRating"
                    (ngModelChange)="updateEditMaxRating($event ?? ratingMax)"
                    [min]="ratingMin"
                    [max]="ratingMax"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    styleClass="w-full"
                />
            </div>
        </div>
    </div>
    }

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            [disabled]="editSaving()"
            (onClick)="closeEdit()"
        />
        <p-button
            label="Save"
            icon="pi pi-check"
            [loading]="editSaving()"
            (onClick)="saveEdit()"
        />
    </ng-template>
</p-dialog>

<p-dialog
    [visible]="deleteConfirmVisible()"
    (visibleChange)="deleteConfirmVisible.set($event)"
    header="Delete this comment?"
    [modal]="true"
    [style]="{ width: '32rem' }"
    [draggable]="false"
    [resizable]="false"
    (onHide)="cancelDelete()"
>
    <div class="flex flex-col gap-2">
        <p class="m-0 text-sm">
            This will permanently remove the comment from the pool.
        </p>
        @if (deleteConfirmComment(); as c) {
        <div
            class="mt-1 text-xs text-muted-color p-3 rounded border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 whitespace-pre-wrap"
        >
            {{ c.Text }}
        </div>
        }
    </div>

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            [disabled]="deleting()"
            (onClick)="cancelDelete()"
        />
        <p-button
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            [loading]="deleting()"
            (onClick)="confirmDelete()"
        />
    </ng-template>
</p-dialog>
